import { apiClient } from './client';
import { normalizePaginated, type PaginatedResult } from './planet';
import type { InboxMessageModel, InboxMessageType, SendInboxMessagePDTO } from '@/types/inbox';

const BASE = '/api/v1/inbox';

export interface InboxQueryParams {
  ownerPlayerUniverseId: number;
  messageType?: InboxMessageType;
  unreadOnly?: boolean;
}

export async function fetchInbox(params: InboxQueryParams): Promise<PaginatedResult<InboxMessageModel>> {
  // ⚠ GET /api/v1/inbox = KController.Get -> renvoie un TABLEAU BRUT (pas un
  // PagedResult, qui est sur /paged). Lire `.items`/`.total` en dur donnait
  // undefined -> liste vide, compteur de non-lus à 0 et toasts qui jetaient en
  // silence (donc AUCUN message d'accomplissement/quête visible). normalizePaginated
  // absorbe le tableau brut -> { items, total }. Cf même piège dans planet.ts.
  const { data } = await apiClient.get(BASE, { params });
  return normalizePaginated<InboxMessageModel>(data);
}

/**
 * Renvoie le nombre de messages non lus. V1 utilise le `total` de la
 * pagination de `fetchInbox` avec unreadOnly=true (pas d'endpoint dedie
 * cote backend). C'est leger : la response ne contient que metadata +
 * la 1ere page, et seul `total` est lu.
 */
export async function getUnreadCount(ownerPlayerUniverseId: number): Promise<number> {
  const r = await fetchInbox({ ownerPlayerUniverseId, unreadOnly: true });
  return r.total ?? 0;
}

export async function fetchInboxMessage(id: number): Promise<InboxMessageModel> {
  const { data } = await apiClient.get<InboxMessageModel>(`${BASE}/${id}`);
  return data;
}

export async function markAsRead(id: number): Promise<void> {
  await apiClient.post(`${BASE}/${id}/read`);
}

/** Marque TOUS les messages non lus de la boite comme lus. Renvoie le nombre marqué. */
export async function markAllAsRead(ownerPlayerUniverseId: number): Promise<number> {
  const { data } = await apiClient.post<{ marked: number }>(
    `${BASE}/read-all`, null, { params: { ownerPlayerUniverseId } },
  );
  return data?.marked ?? 0;
}

/** Supprime les messages de la boite (soft-delete), optionnellement filtrés par
 *  type (messageType absent = toute la boite). Renvoie le nombre supprimé. */
export async function deleteAll(
  ownerPlayerUniverseId: number,
  messageType?: InboxMessageType,
): Promise<number> {
  const { data } = await apiClient.post<{ deleted: number }>(
    `${BASE}/delete-all`, null,
    { params: { ownerPlayerUniverseId, messageType: messageType || undefined } },
  );
  return data?.deleted ?? 0;
}

export async function deleteMessage(id: number): Promise<void> {
  // Route KStone : DELETE {id}/{force} (cf KController). Sans le segment {force}
  // l'URL ne matche que la route GET {id} -> 405. force=false = soft-delete
  // (Status=Deleted, exclu de la liste, garde l'audit).
  await apiClient.delete(`${BASE}/${id}/false`);
}

export async function sendMessage(body: SendInboxMessagePDTO): Promise<InboxMessageModel> {
  const { data } = await apiClient.post<InboxMessageModel>(`${BASE}/messages`, body);
  return data;
}
